import { IonButton, IonIcon } from '@ionic/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { alertCircleOutline, refreshOutline } from 'ionicons/icons';
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface BoundaryFailure {
  error: Error;
  /** Limpa o erro das queries e tenta renderizar os filhos de novo. */
  retry: () => void;
}

interface BoundaryProps {
  children: ReactNode;
  onReset: () => void;
  fallback?: (failure: BoundaryFailure) => ReactNode;
}

interface BoundaryState {
  error: Error | null;
}

class ErrorBoundaryInner extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary capturou:', error, info.componentStack);
    }
  }

  handleRetry = (): void => {
    this.props.onReset();
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback({ error: this.state.error, retry: this.handleRetry });
    }

    const message = this.state.error.message || 'Algo deu errado ao carregar.';

    return (
      <div className="rx-container rx-screen px-6 py-12 flex flex-col items-center text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-negative-tint text-negative mb-5">
          <IonIcon icon={alertCircleOutline} className="text-[24px]" />
        </span>
        <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-negative mb-2">
          Erro inesperado
        </p>
        <h2 className="text-[22px] font-medium tracking-tight text-ink mb-3">
          Não conseguimos carregar esta tela.
        </h2>
        <p className="text-[14px] text-ink-muted max-w-[34ch] mb-6">{message}</p>
        <IonButton onClick={this.handleRetry} className="rx-button">
          <IonIcon icon={refreshOutline} className="mr-2 text-[16px]" />
          Tentar novamente
        </IonButton>
      </div>
    );
  }
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Tela própria para a falha; sem ela, a tela genérica com "Tentar novamente". */
  fallback?: (failure: BoundaryFailure) => ReactNode;
}

export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps): JSX.Element {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundaryInner onReset={reset} fallback={fallback}>
          {children}
        </ErrorBoundaryInner>
      )}
    </QueryErrorResetBoundary>
  );
}
